import { createStorefrontApiClient } from '@shopify/storefront-api-client';

// Shopify API Types
interface Money {
  amount: string;
  currencyCode: string;
}

interface Image {
  id?: string;
  url: string;
  width?: number;
  height?: number;
  altText: string | null;
}

interface ImageEdge {
  node: Image;
}

interface ProductVariant {
  id: string;
  title: string;
  availableForSale: boolean;
  price?: Money;
}

interface ProductVariantEdge {
  node: ProductVariant;
}

interface Product {
  id: string;
  title: string;
  handle: string;
  description: string;
  priceRange: {
    minVariantPrice: Money;
  };
  featuredImage?: Image;
  images: {
    edges: ImageEdge[];
  };
  variants: {
    edges: ProductVariantEdge[];
  };
}

interface ProductEdge {
  node: Product;
}

interface CartLineInput {
  merchandiseId: string;
  quantity: number;
}

interface CartLineUpdateInput {
  id: string;
  merchandiseId?: string;
  quantity?: number;
}

interface CartLine {
  id: string;
  quantity: number;
  merchandise: ProductVariant & {
    product: Product;
  };
}

interface CartLineEdge {
  node: CartLine;
}

interface Cart {
  id: string;
  checkoutUrl: string;
  lines: {
    edges: CartLineEdge[];
  };
  cost: {
    subtotalAmount: Money;
    totalAmount: Money;
  };
}

interface ShopifyResponse<T> {
  data?: T;
  errors?: unknown;
  extensions?: unknown;
}

export const shopifyClient = createStorefrontApiClient({
  apiVersion: '2025-04',
  publicAccessToken: '22858d3fb07f91ddeae41195b9d52c6c',
  storeDomain: 'http://u2kp9b-7b.myshopify.com',
});

export async function getProducts(first: number = 10) {
  const query = `
		query getProducts($first: Int!) {
			products(first: $first) {
				edges {
					node {
						id
						title
						handle
						description
						priceRange {
							minVariantPrice {
								amount
								currencyCode
							}
						}
						images(first: 1) {
							edges {
								node {
									url
									altText
								}
							}
						}
						variants(first: 1) {
							edges {
								node {
									id
									title
									availableForSale
								}
							}
						}
					}
				}
			}
		}
	`;

  const {
    data,
    errors: _errors,
    extensions: _extensions,
  }: ShopifyResponse<{
    products: { edges: ProductEdge[] };
  }> = await shopifyClient.request(query, {
    variables: {
      first,
    },
  });

  if (!data) {
    throw new Error('Failed to fetch products from Shopify');
  }

  return data.products.edges.map(edge => edge.node);
}

export type GetProductsReturn = Awaited<ReturnType<typeof getProducts>>;
export type CreateCartReturn = Awaited<ReturnType<typeof createCart>>;
export type FetchCartReturn = Awaited<ReturnType<typeof fetchCart>>;
export type GetCartReturn = Awaited<ReturnType<typeof getCart>>;

export async function getProductByHandle(handle: string) {
  const query = `
		query getProductByHandle($handle: String!) {
			product(handle: $handle) {
				id
				title
				handle
				description
				priceRange {
					minVariantPrice {
						amount
						currencyCode
					}
				}
				featuredImage {
					id
					url
					width
					height
					altText
				}
				variants(first: 250) {
					edges {
						node {
							id
							title
							availableForSale
							price {
								amount
								currencyCode
							}
						}
					}
				}
			}
		}
	`;

  const {
    data,
    errors: _errors,
    extensions: _extensions,
  }: ShopifyResponse<{ product: Product | null }> = await shopifyClient.request(
    query,
    {
      variables: {
        handle,
      },
    }
  );

  if (!data) {
    return null;
  }

  return data.product;
}

export async function createCart(lines: CartLineInput[] = []) {
  const cartLines = lines.map(item => ({
    merchandiseId: item.merchandiseId,
    quantity: item.quantity,
  }));

  const query = `
    mutation createCart($lines: [CartLineInput!]) {
      cartCreate(input: { lines: $lines }) {
        cart {
          id
          checkoutUrl
          lines(first: 100) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    product {
                      title
                      handle
                      images(first: 1) {
                        edges {
                          node {
                            url
                            altText
                          }
                        }
                      }
                    }
                    price {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
          cost {
            subtotalAmount {
              amount
              currencyCode
            }
            totalAmount {
              amount
              currencyCode
            }
          }
        }
      }
    }
  `;

  const {
    data,
    errors: _errors,
    extensions: _extensions,
  }: ShopifyResponse<{
    cartCreate: { cart: Cart };
  }> = await shopifyClient.request(query, {
    variables: {
      lines: cartLines,
    },
  });

  if (!data) {
    throw new Error('Failed to create cart');
  }

  return data.cartCreate.cart;
}

export async function fetchCart(cartId: string) {
  const query = `
    query getCart($cartId: ID!) {
      cart(id: $cartId) {
        id
        checkoutUrl
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                }
              }
            }
          }
        }
      }
    }
  `;

  const {
    data,
    errors: _errors,
    extensions: _extensions,
  }: ShopifyResponse<{ cart: Cart | null }> = await shopifyClient.request(
    query,
    {
      variables: {
        cartId,
      },
    }
  );

  if (!data) {
    return null;
  }

  return data.cart;
}

export async function addToCart(
  cartId: string,
  lines: CartLineInput[]
): Promise<Cart> {
  const query = `
		mutation addToCart($cartId: ID!, $lines: [CartLineInput!]!) {
			cartLinesAdd(cartId: $cartId, lines: $lines) {
				cart {
					id
					lines(first: 100) {
						edges {
							node {
								id
								quantity
								merchandise {
									... on ProductVariant {
										id
										title
										product {
											title
											handle
											featuredImage {
												id
												url
												width
												height
												altText
											}
											images(first: 1) {
												edges {
													node {
														url
														altText
													}
												}
											}
										}
										price {
											amount
											currencyCode
										}
									}
								}
							}
						}
					}
					cost {
						subtotalAmount {
							amount
							currencyCode
						}
						totalAmount {
							amount
							currencyCode
						}
					}
				}
			}
		}
	`;

  const {
    data,
    errors: _errors,
    extensions: _extensions,
  }: ShopifyResponse<{
    cartLinesAdd: { cart: Cart };
  }> = await shopifyClient.request(query, {
    variables: {
      cartId,
      lines,
    },
  });

  if (!data) {
    throw new Error('Failed to add items to cart');
  }

  return data.cartLinesAdd.cart;
}

export async function removeFromCart(
  cartId: string,
  lineIds: string[]
): Promise<Cart> {
  const query = `
		mutation removeFromCart($cartId: ID!, $lineIds: [ID!]!) {
			cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
				cart {
					id
					lines(first: 100) {
						edges {
							node {
								id
								quantity
								merchandise {
									... on ProductVariant {
										id
										title
										product {
											title
											handle
											images(first: 1) {
												edges {
													node {
														url
														altText
													}
												}
											}
										}
										price {
											amount
											currencyCode
										}
									}
								}
							}
						}
					}
					cost {
						subtotalAmount {
							amount
							currencyCode
						}
						totalAmount {
							amount
							currencyCode
						}
					}
				}
			}
		}
	`;

  const {
    data,
    errors: _errors,
    extensions: _extensions,
  }: ShopifyResponse<{
    cartLinesRemove: { cart: Cart };
  }> = await shopifyClient.request(query, {
    variables: {
      cartId,
      lineIds,
    },
  });

  if (!data) {
    throw new Error('Failed to remove items from cart');
  }

  return data.cartLinesRemove.cart;
}

export async function updateCartQuantity(
  cartId: string,
  lines: CartLineUpdateInput[]
): Promise<Cart> {
  const query = `
		mutation updateCartQuantity($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
			cartLinesUpdate(cartId: $cartId, lines: $lines) {
				cart {
					id
					lines(first: 100) {
						edges {
							node {
								id
								quantity
								merchandise {
									... on ProductVariant {
										id
										title
										product {
											title
											handle
											images(first: 1) {
												edges {
													node {
														url
														altText
													}
												}
											}
										}
										price {
											amount
											currencyCode
										}
									}
								}
							}
						}
					}
					cost {
						subtotalAmount {
							amount
							currencyCode
						}
						totalAmount {
							amount
							currencyCode
						}
					}
				}
			}
		}
	`;

  const {
    data,
    errors: _errors,
    extensions: _extensions,
  }: ShopifyResponse<{
    cartLinesUpdate: { cart: Cart };
  }> = await shopifyClient.request(query, {
    variables: {
      cartId,
      lines,
    },
  });

  if (!data) {
    throw new Error('Failed to update cart quantities');
  }

  return data.cartLinesUpdate.cart;
}

export async function getCart(cartId: string) {
  const query = `
		query getCart($cartId: ID!) {
			cart(id: $cartId) {
				id
				checkoutUrl
				lines(first: 100) {
					edges {
						node {
							id
							quantity
							merchandise {
								... on ProductVariant {
									id
									title
									product {
										title
										handle
										images(first: 1) {
											edges {
												node {
													url
													altText
												}
											}
										}
									}
									price {
										amount
										currencyCode
									}
								}
							}
						}
					}
				}
				cost {
					subtotalAmount {
						amount
						currencyCode
					}
					totalAmount {
						amount
						currencyCode
					}
				}
			}
		}
	`;

  const {
    data,
    errors: _errors,
    extensions: _extensions,
  }: ShopifyResponse<{ cart: Cart | null }> = await shopifyClient.request(
    query,
    {
      variables: {
        cartId,
      },
    }
  );

  if (!data) {
    return null;
  }

  return data.cart;
}
